import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  Upload, 
  Save, 
  Eye, 
  Download, 
  Trash2, 
  Move, 
  Type,
  Palette,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlertCircle,
  Check,
  FileImage,
  Settings
} from 'lucide-react';
import { CredentialTemplate, CredentialSchema, TemplateFieldPosition } from '../../types/W3CCredentialTypes';

interface TemplateDesignerProps {
  template?: CredentialTemplate;
  schema: CredentialSchema;
  onSave: (template: CredentialTemplate) => void;
  onCancel: () => void;
}

interface DragState {
  isDragging: boolean;
  fieldName: string;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

export default function CredentialTemplateDesigner({ template, schema, onSave, onCancel }: TemplateDesignerProps) {
  const [templateData, setTemplateData] = useState<Partial<CredentialTemplate>>({
    name: '',
    description: '',
    schemaId: schema.id,
    schemaVersion: schema.version,
    backgroundImage: '',
    width: 800,
    height: 600,
    fieldPositions: [],
    isActive: true
  });

  const [uploadedImage, setUploadedImage] = useState<string>('');
  const [selectedField, setSelectedField] = useState<string>('');
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    fieldName: '',
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPreview, setShowPreview] = useState(false);

  const canvasRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (template) {
      setTemplateData(template);
      setUploadedImage(template.backgroundImage);
    }
  }, [template]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target?.result as string;
        setUploadedImage(imageUrl);
        setTemplateData(prev => ({ ...prev, backgroundImage: imageUrl }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCanvasClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!selectedField || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const existingIndex = templateData.fieldPositions?.findIndex(
      pos => pos.fieldName === selectedField
    );

    const newPosition: TemplateFieldPosition = {
      fieldName: selectedField,
      x: x,
      y: y,
      width: 200,
      height: 30,
      fontSize: 14,
      fontFamily: 'Inter',
      fontWeight: 'normal',
      color: '#1E293B',
      alignment: 'left'
    };

    if (existingIndex !== -1) {
      // Update existing position
      setTemplateData(prev => ({
        ...prev,
        fieldPositions: prev.fieldPositions?.map((pos, index) => 
          index === existingIndex ? newPosition : pos
        ) || [newPosition]
      }));
    } else {
      // Add new position
      setTemplateData(prev => ({
        ...prev,
        fieldPositions: [...(prev.fieldPositions || []), newPosition]
      }));
    }

    setSelectedField('');
  };

  const handleFieldDrag = (fieldName: string, event: React.MouseEvent) => {
    event.preventDefault();
    setDragState({
      isDragging: true,
      fieldName,
      startX: event.clientX,
      startY: event.clientY,
      currentX: event.clientX,
      currentY: event.clientY
    });
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (dragState.isDragging && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      setDragState(prev => ({
        ...prev,
        currentX: event.clientX,
        currentY: event.clientY
      }));

      // Update field position
      setTemplateData(prev => ({
        ...prev,
        fieldPositions: prev.fieldPositions?.map(pos => 
          pos.fieldName === dragState.fieldName 
            ? { ...pos, x, y }
            : pos
        ) || []
      }));
    }
  };

  const handleMouseUp = () => {
    setDragState(prev => ({
      ...prev,
      isDragging: false,
      fieldName: ''
    }));
  };

  const updateFieldStyle = (fieldName: string, updates: Partial<TemplateFieldPosition>) => {
    setTemplateData(prev => ({
      ...prev,
      fieldPositions: prev.fieldPositions?.map(pos => 
        pos.fieldName === fieldName 
          ? { ...pos, ...updates }
          : pos
      ) || []
    }));
  };

  const removeFieldPosition = (fieldName: string) => {
    setTemplateData(prev => ({
      ...prev,
      fieldPositions: prev.fieldPositions?.filter(pos => pos.fieldName !== fieldName) || []
    }));
  };

  const validateTemplate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!templateData.name) {
      newErrors.name = 'Template name is required';
    }

    if (!templateData.description) {
      newErrors.description = 'Template description is required';
    }

    if (!templateData.backgroundImage) {
      newErrors.backgroundImage = 'Background image is required';
    }

    // Check if all required fields are mapped
    const requiredFields = schema.fields.filter(field => field.required);
    const mappedFields = templateData.fieldPositions?.map(pos => pos.fieldName) || [];
    const unmappedRequired = requiredFields.filter(field => !mappedFields.includes(field.name));

    if (unmappedRequired.length > 0) {
      newErrors.requiredFields = `Required fields not mapped: ${unmappedRequired.map(f => f.displayName).join(', ')}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateTemplate()) {
      return;
    }

    const now = new Date().toISOString();
    const savedTemplate: CredentialTemplate = {
      id: template?.id || `template_${Date.now()}`,
      name: templateData.name!,
      description: templateData.description!,
      schemaId: schema.id,
      schemaVersion: schema.version,
      backgroundImage: templateData.backgroundImage!,
      width: templateData.width || 800,
      height: templateData.height || 600,
      fieldPositions: templateData.fieldPositions || [],
      createdAt: template?.createdAt || now,
      updatedAt: now,
      createdBy: 'current-user',
      organizationId: 'current-org',
      isActive: templateData.isActive || true
    };

    onSave(savedTemplate);
  };

  const generatePreviewData = () => {
    const previewData: Record<string, any> = {};
    schema.fields.forEach(field => {
      switch (field.type) {
        case 'text':
          previewData[field.name] = `Sample ${field.displayName}`;
          break;
        case 'number':
          previewData[field.name] = '42';
          break;
        case 'date':
          previewData[field.name] = '2024-01-01';
          break;
        case 'email':
          previewData[field.name] = 'sample@example.com';
          break;
        default:
          previewData[field.name] = `Sample ${field.displayName}`;
      }
    });
    return previewData;
  };

  const exportTemplate = () => {
    const dataStr = JSON.stringify(templateData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `${templateData.name?.replace(/\s+/g, '_')}_template.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const selectedFieldPosition = templateData.fieldPositions?.find(pos => pos.fieldName === selectedField);
  const previewData = generatePreviewData();

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-charcoal-ink">
            Template Designer
          </h1>
          <p className="text-warm-slate">
            Design the visual layout for {schema.name} credentials
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={exportTemplate} disabled={!templateData.name}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowPreview(!showPreview)}
            disabled={!templateData.backgroundImage}
          >
            <Eye className="h-4 w-4 mr-2" />
            {showPreview ? 'Edit' : 'Preview'}
          </Button>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} className="credential-capital-button-primary">
            <Save className="h-4 w-4 mr-2" />
            Save Template
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Template Configuration */}
        <Card className="credential-capital-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Settings className="h-5 w-5 mr-2" />
              Template Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="template-name">Template Name *</Label>
              <Input
                id="template-name"
                placeholder="e.g., Degree Certificate"
                value={templateData.name}
                onChange={(e) => setTemplateData(prev => ({ ...prev, name: e.target.value }))}
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            <div>
              <Label htmlFor="template-description">Description *</Label>
              <Textarea
                id="template-description"
                placeholder="Describe this template"
                value={templateData.description}
                onChange={(e) => setTemplateData(prev => ({ ...prev, description: e.target.value }))}
                className={errors.description ? 'border-red-500' : ''}
                rows={3}
              />
              {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
            </div>

            <div>
              <Label>Background Image *</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="w-full"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload Image
              </Button>
              {errors.backgroundImage && <p className="text-red-500 text-xs mt-1">{errors.backgroundImage}</p>}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="template-width">Width</Label>
                <Input
                  id="template-width"
                  type="number"
                  value={templateData.width}
                  onChange={(e) => setTemplateData(prev => ({ ...prev, width: parseInt(e.target.value) }))}
                />
              </div>
              <div>
                <Label htmlFor="template-height">Height</Label>
                <Input
                  id="template-height"
                  type="number"
                  value={templateData.height}
                  onChange={(e) => setTemplateData(prev => ({ ...prev, height: parseInt(e.target.value) }))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Field Selector */}
        <Card className="credential-capital-card">
          <CardHeader>
            <CardTitle>Schema Fields</CardTitle>
            <CardDescription>Click a field, then click on the canvas to position it</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {schema.fields.map(field => {
                const isPositioned = templateData.fieldPositions?.some(pos => pos.fieldName === field.name);
                const isSelected = selectedField === field.name;
                
                return (
                  <div
                    key={field.name}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      isSelected 
                        ? 'border-electric-steel bg-electric-steel/10' 
                        : isPositioned
                        ? 'border-verified-green bg-verified-green/10'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedField(isSelected ? '' : field.name)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{field.displayName}</p>
                        <p className="text-xs text-warm-slate">{field.name}</p>
                      </div>
                      <div className="flex items-center space-x-1">
                        {field.required && (
                          <Badge className="bg-red-100 text-red-800 text-xs">Required</Badge>
                        )}
                        {isPositioned && (
                          <Badge className="bg-verified-green text-white text-xs">
                            <Check className="h-3 w-3" />
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {errors.requiredFields && (
              <Alert className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  {errors.requiredFields}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Canvas */}
        <div className="lg:col-span-2">
          <Card className="credential-capital-card">
            <CardHeader>
              <CardTitle>Template Canvas</CardTitle>
              <CardDescription>
                {selectedField ? `Click to position "${selectedField}" field` : 'Select a field to position it'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-hidden">
                <div
                  ref={canvasRef}
                  className="relative bg-gray-100 cursor-crosshair"
                  style={{
                    width: templateData.width || 800,
                    height: templateData.height || 600,
                    backgroundImage: uploadedImage ? `url(${uploadedImage})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                  onClick={handleCanvasClick}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                >
                  {/* Field Positions */}
                  {templateData.fieldPositions?.map(position => (
                    <div
                      key={position.fieldName}
                      className="absolute border-2 border-electric-steel bg-white/90 rounded px-2 py-1 cursor-move"
                      style={{
                        left: position.x,
                        top: position.y,
                        width: position.width,
                        height: position.height,
                        fontSize: position.fontSize,
                        fontFamily: position.fontFamily,
                        fontWeight: position.fontWeight,
                        color: position.color,
                        textAlign: position.alignment,
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      onMouseDown={(e) => handleFieldDrag(position.fieldName, e)}
                    >
                      <div className="flex-1 truncate">
                        {showPreview 
                          ? previewData[position.fieldName] 
                          : position.fieldName
                        }
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-4 w-4 p-0 ml-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFieldPosition(position.fieldName);
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}

                  {/* Empty State */}
                  {!uploadedImage && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center text-warm-slate">
                        <FileImage className="h-16 w-16 mx-auto mb-4 opacity-50" />
                        <p>Upload a background image to get started</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Field Styling */}
          {selectedFieldPosition && (
            <Card className="credential-capital-card mt-4">
              <CardHeader>
                <CardTitle>Field Styling: {selectedFieldPosition.fieldName}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Width</Label>
                    <Input
                      type="number"
                      value={selectedFieldPosition.width}
                      onChange={(e) => updateFieldStyle(selectedFieldPosition.fieldName, { width: parseInt(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label>Height</Label>
                    <Input
                      type="number"
                      value={selectedFieldPosition.height}
                      onChange={(e) => updateFieldStyle(selectedFieldPosition.fieldName, { height: parseInt(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label>Font Size</Label>
                    <Input
                      type="number"
                      value={selectedFieldPosition.fontSize}
                      onChange={(e) => updateFieldStyle(selectedFieldPosition.fieldName, { fontSize: parseInt(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label>Font Weight</Label>
                    <Select
                      value={selectedFieldPosition.fontWeight}
                      onValueChange={(value) => updateFieldStyle(selectedFieldPosition.fieldName, { fontWeight: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="bold">Bold</SelectItem>
                        <SelectItem value="600">Semi-bold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Color</Label>
                    <Input
                      type="color"
                      value={selectedFieldPosition.color}
                      onChange={(e) => updateFieldStyle(selectedFieldPosition.fieldName, { color: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Alignment</Label>
                    <div className="flex space-x-1">
                      {['left', 'center', 'right'].map(align => (
                        <Button
                          key={align}
                          variant={selectedFieldPosition.alignment === align ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => updateFieldStyle(selectedFieldPosition.fieldName, { alignment: align as any })}
                        >
                          {align === 'left' && <AlignLeft className="h-4 w-4" />}
                          {align === 'center' && <AlignCenter className="h-4 w-4" />}
                          {align === 'right' && <AlignRight className="h-4 w-4" />}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}